import type { IFloatingFilter, IFloatingFilterParams, SuppressHeaderKeyboardEventParams } from "ag-grid-community";
import type { AppContext, Directive, Ref, VNode } from "vue";
import { createApp, h, ref, render, shallowRef } from "vue";

import { componentsI18n } from "../../../i18n";
import type { ColFilterGroup } from "../../columnFilter/filterModel";
import SCAColumnFilter from "../../columnFilter/SCAColumnFilter.vue";
import { resolveFilterColumnKey, toColumnFilterConfig } from "./floatingFilterParams";

const floatingFilterAppContext: AppContext = (() => {
  const contextApp = createApp({ render: () => null });
  contextApp.use(componentsI18n);
  return contextApp._context;
})();

/**
 * The floating filters render in their own app, so the host's global directives (e.g. `tooltip`,
 * used by `SCALabel`) have to be handed over. Call it before the first grid mounts.
 */
export function registerFloatingFilterDirective(name: string, directive: Directive) {
  floatingFilterAppContext.directives[name] = directive;
}

/**
 * For `ColDef.suppressHeaderKeyboardEvent`: the header cell's own Tab handling also lands on
 * tabindex="-1" and inert elements, so a Tab inside a filter popup is left to the browser.
 */
export function isTabInsideFilterPopup({ event }: Pick<SuppressHeaderKeyboardEventParams, "event">): boolean {
  return event.key === "Tab" && event.target instanceof Element && event.target.closest("[popover]") !== null;
}

export class SCAFloatingFilterWrapper implements IFloatingFilter {
  private eGui!: HTMLDivElement;
  private container!: HTMLDivElement;
  private params!: IFloatingFilterParams;
  private paramsRef = shallowRef<IFloatingFilterParams | null>(null);
  private vnodeRoot!: VNode;

  private modelRef: Ref<ColFilterGroup[] | null> = ref(null);

  init(params: IFloatingFilterParams) {
    this.params = params;
    this.paramsRef.value = params;

    this.eGui = document.createElement("div");
    this.container = document.createElement("div");
    this.eGui.appendChild(this.container);

    this.modelRef.value = (params.currentParentModel() as ColFilterGroup[] | null) ?? null;

    this.renderComponent();
  }

  private renderComponent() {
    const onModelChange = (filters: ColFilterGroup[] | null) => {
      this.params.parentFilterInstance((parent) => {
        parent.setModel(filters);
      });
    };

    // Reading modelRef and paramsRef inside the render function makes the grid's model pushes and
    // refreshed params (options that load after the first render) re-render the filter.
    this.vnodeRoot = h({
      render: () => {
        const params = this.paramsRef.value!;
        return h(SCAColumnFilter, {
          colKey: resolveFilterColumnKey(params),
          config: toColumnFilterConfig(params),
          model: this.modelRef.value,
          onModelChange,
        });
      },
    });
    this.vnodeRoot.appContext = floatingFilterAppContext;
    render(this.vnodeRoot, this.container);
  }

  refresh(params: IFloatingFilterParams): boolean {
    this.params = params;
    this.paramsRef.value = params;
    return true;
  }

  onParentModelChanged(parentModel: ColFilterGroup[] | null): void {
    this.modelRef.value = parentModel;
  }

  getGui() {
    return this.eGui;
  }

  private focusHandler?: (e: FocusEvent) => void;

  /**
   * Called by AG Grid after the floating filter GUI has been attached to the DOM.
   * We use this hook to implement focus redirection: AG Grid intentionally focuses
   * the header cell container (.ag-header-cell) for keyboard navigation and accessibility
   * after any UI changes (e.g., when the user enters search text).
   * However, for our custom floating filters, focus should return to the element that held it —
   * the input the user is typing in, or the list option checkbox they just clicked.
   */
  afterGuiAttached(params?: { suppressFocus?: boolean }): void {
    if (params?.suppressFocus) {
      return;
    }

    // Find the parent header cell by traversing up the DOM tree
    let headerCell: HTMLElement | null = this.eGui;
    while (headerCell && !headerCell.classList.contains("ag-header-cell")) {
      headerCell = headerCell.parentElement;
    }

    if (!headerCell) {
      return;
    }

    // Define the focus redirection handler
    this.focusHandler = (e: FocusEvent) => {
      const from = e.relatedTarget as HTMLElement | null;

      // Skip focus redirection unless the focus originated from our floating filter input.
      if (!from || !this.eGui.contains(from)) {
        return;
      }

      if (e.target === headerCell) {
        from.focus();
      }
    };

    headerCell.addEventListener("focusin", this.focusHandler);
  }

  destroy(): void {
    render(null, this.container);

    // Remove the focus listener to prevent memory leaks
    if (this.focusHandler) {
      let headerCell: HTMLElement | null = this.eGui;
      while (headerCell && !headerCell.classList.contains("ag-header-cell")) {
        headerCell = headerCell.parentElement;
      }
      if (headerCell && this.focusHandler) {
        headerCell.removeEventListener("focusin", this.focusHandler);
      }
    }
  }
}
